import Vue from 'vue'

import { FEATURE_FLAG_KEYS, findFeatureFlagDefinition } from '@/utils/featureFlagKeys'
import { authStateOf, buildEvaluationContext, contextSignature, isAnonymousContext } from '@/utils/featureFlagContext'

/**
 * Datadog feature flags in the browser, through OpenFeature.
 *
 *   this.$featureFlag(FEATURE_FLAG_KEYS.newNav.key)          // the key's own fallback
 *   this.$featureFlag(FEATURE_FLAG_KEYS.newNav.key, false)   // an explicit one
 *   computed: { ...mapFeatureFlags({ showNewNav: FEATURE_FLAG_KEYS.newNav.key }) }
 *
 * Reads are synchronous and reactive. A flag is evaluated the first time something reads it after
 * Datadog has answered for the session's context, so Datadog logs an exposure only for a flag a view
 * actually used. Until then, and for good when Datadog is blocked, down or not configured, a read
 * is its fallback, and the fallback is today's UI. A context change keeps the values already read
 * until Datadog answers for the new context, rather than flashing them back to their fallbacks;
 * signing out drops them at once.
 *
 * A signed-out visitor is never sent to Datadog: no flag targets one, so every read on a public
 * page is its fallback. A visible tab asks Datadog again every `refreshIntervalMs`, which is how a
 * flag changed in Datadog reaches a tab that stays open.
 *
 * The state lives in this plugin, not in Vuex: the login page's store reset would wipe it while the
 * provider carries on.
 *
 * A flag read here only decides what the UI shows. The context comes from client state the user
 * can edit, so anything that must hold regardless is gated on the backend with the same key.
 */

// What the plugin is serving, in OpenFeature's status names plus DISABLED for a build that never
// starts the provider. READY and STALE mean reads are evaluated by Datadog for the session's
// context. This is not the provider's own status, which stays READY while a login is still loading
// the account and goes RECONCILING while an open tab refreshes the context it is serving.
export const FEATURE_FLAG_STATUS = Object.freeze({
  DISABLED: 'DISABLED',
  NOT_READY: 'NOT_READY',
  READY: 'READY',
  STALE: 'STALE',
  RECONCILING: 'RECONCILING',
  ERROR: 'ERROR',
  FATAL: 'FATAL'
})

// Why the status is DISABLED.
export const FEATURE_FLAG_DISABLED_REASON = Object.freeze({
  // VUE_APP_DD_CLIENT_TOKEN or VUE_APP_DD_ENV is empty, as in local development.
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  // FEATURE_FLAG_KEYS is empty, so nothing would read Datadog's answer.
  NO_FLAGS: 'NO_FLAGS'
})

const SERVICE = 'ringba-x-client-ui'

const EVALUATORS = Object.freeze({
  boolean: 'getBooleanDetails',
  string: 'getStringDetails',
  number: 'getNumberDetails'
})

// Where @datadog/openfeature-browser 2.0 keeps each context's configuration between page loads
// (its `IndexedDBFlagsCache`). The entries hold the user and account ids, so they go with the session.
const PROVIDER_CACHE_DATABASE = 'dd-flagging'

// What Datadog last said for the context the plugin sent.
const ANSWER = Object.freeze({
  // A configuration fetched for it.
  FRESH: 'FRESH',
  // The fetch failed, and the provider answered from the configuration it holds or has cached for
  // that context.
  CACHED: 'CACHED',
  // The fetch failed with nothing cached, so the provider holds no configuration for it.
  NONE: 'NONE'
})

const DEFAULT_TIMINGS = Object.freeze({
  // How long a signed-in user's context waits for the current account before going without it.
  // Login and a page load fetch the account straight after the user, so waiting saves a Datadog
  // round trip and keeps the rollout bucket the account's. A route that loads no account (the super
  // admin's Impersonate page) sends the user's context once this runs out.
  accountWaitMs: 2000,

  // How often a visible tab asks Datadog again for the context it is in. The provider only asks
  // when the context changes, so without this a flag changed in Datadog (a kill switch, a rollout
  // step) would not reach a tab until it reloads. It is also how a context Datadog did not answer
  // for is asked again. A hidden tab does not ask, and asks at once on becoming visible if the
  // interval has passed. Datadog bills each ask as a flag configuration request.
  refreshIntervalMs: 15 * 60 * 1000,

  // Each flag configuration request gives up after this long, and a network error, a timeout, a
  // 408 or a 5xx is retried this many times with jittered backoff (Datadog's `withTimeout` and
  // `withRetry`).
  requestTimeoutMs: 10000,
  requestRetries: 3
})

const warnedUnknownKeys = new Set()

function readConfig() {
  return {
    clientToken: process.env.VUE_APP_DD_CLIENT_TOKEN,
    env: process.env.VUE_APP_DD_ENV,
    site: process.env.VUE_APP_DD_SITE || undefined,
    applicationId: process.env.VUE_APP_DD_APPLICATION_ID || undefined
  }
}

function errorMessage(error) {
  return error?.message || String(error)
}

function isServing(status) {
  return status === FEATURE_FLAG_STATUS.READY || status === FEATURE_FLAG_STATUS.STALE
}

// Datadog turned the request down (a revoked token, a malformed request), so asking again for the
// same context gets the same answer. A 408 or a 429 is worth asking again later.
function isRefusal(status) {
  return status >= 400 && status < 500 && status !== 408 && status !== 429
}

function forgetCachedConfigurations() {
  try {
    window.indexedDB?.deleteDatabase(PROVIDER_CACHE_DATABASE)
  } catch (error) {
    // Some private windows refuse IndexedDB outright, and then nothing was cached either.
  }
}

/**
 * The flags for one store: the reactive state readers depend on and, once Datadog's code has
 * loaded, the OpenFeature client behind it. `install` creates one; the tests drive it directly.
 *
 * @param {import('vuex').Store} store
 * @param {object} [options]
 * @param {object} [options.config] the Datadog settings; the build's environment by default
 * @param {number} [options.accountWaitMs]
 * @param {number} [options.refreshIntervalMs]
 * @param {number} [options.requestTimeoutMs]
 * @param {number} [options.requestRetries]
 */
export function createFeatureFlags(store, { config = readConfig(), ...timings } = {}) {
  const { accountWaitMs, refreshIntervalMs, requestTimeoutMs, requestRetries } = { ...DEFAULT_TIMINGS, ...timings }

  const state = Vue.observable({
    status: FEATURE_FLAG_STATUS.NOT_READY,

    // A FEATURE_FLAG_DISABLED_REASON while the status is DISABLED, and null otherwise.
    disabledReason: null,

    lastError: null,

    // The context last handed to OpenFeature, as `contextSignature` prints it, and null while
    // signed out.
    contextSignature: null,

    // key -> { value } or { errorCode }: what each flag evaluated to for the configuration being
    // served, filled on first read. Vue does not look inside a Map, so filling it re-renders
    // nothing; replacing it is what re-renders every reader.
    results: new Map(),

    // How many flags have been evaluated, so the diagnostics panel notices a key that another view
    // evaluated after the panel rendered.
    evaluations: 0
  })

  let sdk = null
  let client = null
  let provider = null
  let providerSet = false
  let unwatch = null
  let stopped = false
  let fatal = false

  // The context last handed to OpenFeature. Null before the first one and once signed out.
  let applied = null
  let appliedSignature = null

  // Whether the session is in `applied`. While a login or a page load is still fetching the user
  // or the account, it has left it for a context that has not been sent yet.
  let inContext = false
  let signedOut = false

  // Numbers each request to OpenFeature, so that only the latest one's outcome counts.
  let request = 0
  let inFlight = false
  let settledAt = 0

  // An ANSWER, or null until Datadog has answered for `applied`.
  let answer = null
  // Datadog refused the latest request for `applied`, so it is not asked again for it.
  let refused = false
  // Numbers each configuration the provider has held, so `results` can tell whether it was
  // evaluated against the one held now.
  let configuration = 0
  let resultsConfiguration = 0

  // What went wrong with the latest request's fetch. The provider does not say: it answers from
  // its cache without an error, and its error does not carry the HTTP status.
  let fetchFailure = null

  // Whether a read may evaluate: the provider holds Datadog's answer for `applied`, and the session
  // is in it. Not otherwise: the provider's resolve ignores the context the SDK passes it and
  // answers from its last configuration, which during a context change belongs to the previous one.
  let canEvaluate = false
  // Whether a context has been served since the session began, which tells a context change
  // (RECONCILING) from the session's first wait (NOT_READY).
  let servedBefore = false

  let evaluations = 0
  let accountWait = null
  let refreshTimer = null

  const currentContext = () => buildEvaluationContext(authStateOf(store))

  const holdsAnswer = () => answer === ANSWER.FRESH || answer === ANSWER.CACHED

  /**
   * The value behind `$featureFlag`. A key missing from `FEATURE_FLAG_KEYS` is a bug: it throws
   * outside production builds so a unit test or a dev session trips over it, and in production it
   * warns once and returns the fallback.
   */
  function read(key, fallback) {
    const definition = findFeatureFlagDefinition(key)

    if (!definition) {
      const message = `Unknown feature flag key "${key}". Add it to FEATURE_FLAG_KEYS in src/utils/featureFlagKeys.js (and, once it exists, to the RingbaXFlags registry in ringba-x).`

      if (process.env.NODE_ENV !== 'production') {
        throw new Error(message)
      }

      if (!warnedUnknownKeys.has(key)) {
        warnedUnknownKeys.add(key)
        console.warn(message)
      }

      return fallback
    }

    const result = resultFor(definition)

    if (result && !result.errorCode) {
      return result.value
    }

    return fallback === undefined ? definition.fallback : fallback
  }

  function resultFor({ key, type, fallback }) {
    // Reading `state.results` is what re-renders the caller when the values change.
    const { results } = state

    if (!results.has(key) && canEvaluate) {
      // The *Details evaluators never throw: a failure comes back as the default value with an
      // error code. The evaluation is also what logs the exposure.
      const details = client[EVALUATORS[type]](key, fallback)

      results.set(key, details.errorCode ? { errorCode: details.errorCode } : { value: details.value })

      // Assigned rather than incremented, so the caller does not come to depend on the count.
      evaluations += 1
      state.evaluations = evaluations
    }

    return results.get(key)
  }

  /**
   * What each flag reads as now and where that comes from, for the diagnostics panel. It evaluates
   * nothing, since an evaluation logs an exposure.
   */
  function inspect() {
    const { results } = state

    // Vue does not track the Map's entries, so depend on the evaluation count as well.
    void state.evaluations

    return Object.values(FEATURE_FLAG_KEYS).map(({ key, fallback }) => {
      const result = results.get(key)

      if (result && !result.errorCode) {
        return { key, value: result.value, source: 'Datadog' }
      }

      return { key, value: fallback, source: `Fallback (${result ? result.errorCode : 'not evaluated'})` }
    })
  }

  function dropResults() {
    if (state.results.size) {
      state.results = new Map()
    }
  }

  // Works out whether reads may evaluate, and the status that says so. Called after every change
  // to the session's context or to what Datadog has said for it.
  function update() {
    const serving = !fatal && inContext && holdsAnswer()

    // The values read so far were evaluated against another configuration: they are evaluated
    // again as they are read, which re-renders every reader once.
    if (serving && resultsConfiguration !== configuration) {
      resultsConfiguration = configuration
      state.results = new Map()
    }

    canEvaluate = serving
    servedBefore = servedBefore || serving

    if (fatal) {
      state.status = FEATURE_FLAG_STATUS.FATAL
    } else if (serving) {
      state.status = answer === ANSWER.CACHED ? FEATURE_FLAG_STATUS.STALE : FEATURE_FLAG_STATUS.READY
    } else if (inContext && answer === ANSWER.NONE) {
      state.status = FEATURE_FLAG_STATUS.ERROR
    } else if (servedBefore) {
      state.status = FEATURE_FLAG_STATUS.RECONCILING
    } else {
      state.status = FEATURE_FLAG_STATUS.NOT_READY
    }
  }

  // The provider's flag configuration requests, through Datadog's own helpers: a request that
  // hangs times out rather than holding up the context change for good, and a network error, a
  // timeout, a 408 or a 5xx is retried with backoff. What went wrong is kept for `settle`.
  function configurationFetch({ withRetry, withTimeout }) {
    const fetchWithRetries = withRetry(withTimeout((input, init) => window.fetch(input, init), requestTimeoutMs), requestRetries)

    return async (input, init) => {
      // The provider calls this as the request is made, and a newer request aborts this one.
      const forRequest = request

      try {
        const response = await fetchWithRetries(input, init)

        if (!response.ok && forRequest === request) {
          fetchFailure = { message: `Datadog answered ${response.status} ${response.statusText}`.trim(), refused: isRefusal(response.status) }
        }

        return response
      } catch (error) {
        if (forRequest === request) {
          fetchFailure = { message: errorMessage(error), refused: false }
        }

        throw error
      }
    }
  }

  // Hands `applied` to OpenFeature, which asks Datadog for it.
  function ask() {
    if (fatal) {
      return
    }

    const thisRequest = ++request

    clearTimeout(refreshTimer)
    refreshTimer = null
    inFlight = true
    fetchFailure = null

    // Not awaited by anyone: the app runs on what it has until the answer arrives.
    const sending = providerSet
      ? sdk.OpenFeature.setContext(applied)
      : sdk.OpenFeature.setProviderAndWait(provider, applied)

    providerSet = true
    update()

    sending.then(
      () => settle(thisRequest),
      error => settle(thisRequest, error)
    )
  }

  // OpenFeature settles a context change whether or not Datadog answered, and only its status
  // tells them apart. A failed initialisation rejects instead.
  function settle(thisRequest, error) {
    // A newer request is on its way, or the session has signed out since.
    if (thisRequest !== request) {
      return
    }

    inFlight = false
    settledAt = Date.now()

    const status = client.providerStatus

    if (status === FEATURE_FLAG_STATUS.FATAL) {
      fatal = true
      state.lastError = failureMessage(error)
    } else if (!error && isServing(status)) {
      answered()
    } else {
      notAnswered(error)
    }

    update()
    scheduleRefresh()
  }

  function answered() {
    if (!fetchFailure) {
      answer = ANSWER.FRESH
      configuration += 1
      refused = false
      state.lastError = null

      return
    }

    // The fetch failed and the provider answered from cache. For a context it already held, that
    // is the configuration it had, so the values already read still hold.
    if (!holdsAnswer()) {
      configuration += 1
    }

    answer = ANSWER.CACHED
    refused = fetchFailure.refused
    state.lastError = fetchFailure.message
  }

  // Datadog did not answer for `applied`, and the provider has nothing cached for it. What it does
  // hold belongs to an earlier context, so every read falls back until Datadog answers.
  function notAnswered(error) {
    answer = ANSWER.NONE
    refused = Boolean(fetchFailure?.refused)
    state.lastError = failureMessage(error)
    dropResults()
  }

  function failureMessage(error) {
    return fetchFailure?.message || (error ? errorMessage(error) : 'Datadog did not answer for this context')
  }

  // Asks again for the context last sent once `refreshIntervalMs` has passed since its last answer,
  // while the tab is visible.
  function scheduleRefresh() {
    clearTimeout(refreshTimer)
    refreshTimer = null

    if (!applied || inFlight || refused || fatal || document.hidden) {
      return
    }

    refreshTimer = setTimeout(() => {
      refreshTimer = null

      // Between contexts, the next one is about to be sent anyway.
      if (inContext) {
        ask()
      }
    }, Math.max(settledAt + refreshIntervalMs - Date.now(), 0))
  }

  function apply(context) {
    applied = context
    appliedSignature = contextSignature(context)
    state.contextSignature = appliedSignature
    inContext = true
    answer = null
    refused = false

    ask()
  }

  // Nothing is sent for a signed-out visitor, and nothing evaluated for the previous user survives
  // to the login page, in memory or in the provider's cache. The provider itself keeps the previous
  // user's configuration until the next login replaces it, and nothing is evaluated against it.
  function signOut() {
    signedOut = true
    // An answer still on its way is not served.
    request += 1
    inFlight = false
    clearTimeout(refreshTimer)
    refreshTimer = null

    applied = null
    appliedSignature = null
    state.contextSignature = null
    inContext = false
    answer = null
    refused = false
    servedBefore = false
    state.lastError = null

    dropResults()
    forgetCachedConfigurations()
    update()
  }

  // On start, and whenever the auth state behind the context changes.
  function sync() {
    clearTimeout(accountWait)
    accountWait = null

    const context = currentContext()

    if (context && isAnonymousContext(context)) {
      if (!signedOut) {
        signOut()
      }

      return
    }

    signedOut = false

    // Still in, or back in, the context last sent: Datadog has answered for it or is about to.
    if (context && contextSignature(context) === appliedSignature) {
      inContext = true
      update()
      scheduleRefresh()

      return
    }

    // `null` is a session whose user details are still loading, and a signed-in user with no
    // account may be about to get one. Nothing is sent yet, and nothing new is evaluated against
    // the context last sent, which is no longer the session's. The values already read stay.
    inContext = false
    update()

    if (!context) {
      return
    }

    if (!context.accountId) {
      accountWait = setTimeout(() => apply(context), accountWaitMs)

      return
    }

    apply(context)
  }

  function disable(reason) {
    state.disabledReason = reason
    state.status = FEATURE_FLAG_STATUS.DISABLED
  }

  async function start() {
    if (!config.clientToken || !config.env) {
      const missing = [
        !config.clientToken && 'VUE_APP_DD_CLIENT_TOKEN',
        !config.env && 'VUE_APP_DD_ENV'
      ].filter(Boolean)

      console.warn(`Feature flags are off: ${missing.join(' and ')} not set for this build. Every flag uses its fallback.`)

      disable(FEATURE_FLAG_DISABLED_REASON.NOT_CONFIGURED)

      return
    }

    // Datadog bills every flag configuration request, and the provider makes one for each context
    // the plugin sends. With no flag to read, nothing would use the answers. A production build
    // says nothing about it, since nothing is wrong.
    if (!Object.keys(FEATURE_FLAG_KEYS).length) {
      if (process.env.NODE_ENV !== 'production') {
        console.info('Feature flags are off: FEATURE_FLAG_KEYS is empty, so Datadog is not contacted.')
      }

      disable(FEATURE_FLAG_DISABLED_REASON.NO_FLAGS)

      return
    }

    try {
      // Fetched only when Datadog is configured and a flag exists, and off the startup path: the app
      // mounts on fallbacks meanwhile.
      const [openFeature, datadog] = await Promise.all([
        import(/* webpackChunkName: "feature-flags" */ '@openfeature/web-sdk'),
        // The default entry point: Datadog evaluates the rules server-side and returns precomputed
        // assignments for the context, so rule contents (account id lists) never reach the
        // browser. Never import `@datadog/openfeature-browser/rules-based`.
        import(/* webpackChunkName: "feature-flags" */ '@datadog/openfeature-browser')
      ])

      // Throws on an unsupported VUE_APP_DD_SITE, among others. That must not take the app down.
      provider = new datadog.DatadogProvider({
        clientToken: config.clientToken,
        env: config.env,
        site: config.site,
        service: SERVICE,
        applicationId: config.applicationId,
        enableExposureLogging: true,
        flagConfigurationFetch: configurationFetch(datadog)
      })
      sdk = openFeature
    } catch (error) {
      console.warn('Feature flags are off: the Datadog provider could not be loaded or created. Every flag uses its fallback.', error)

      state.lastError = errorMessage(error)
      state.status = FEATURE_FLAG_STATUS.ERROR

      return
    }

    if (stopped) {
      return
    }

    client = sdk.OpenFeature.getClient()

    document.addEventListener('visibilitychange', scheduleRefresh)

    // Login lands here: the login page sets the tokens, the user and then the account. So does
    // logout, which drops the tokens, and `buildEvaluationContext` turns that into the anonymous
    // context. An account switch, impersonation start and impersonation stop all reload the page,
    // so the `sync()` below covers them.
    unwatch = store.watch(currentContext, sync)
    sync()
  }

  // Tears the plugin down. Only the tests need this.
  async function stop() {
    stopped = true
    // An answer still on its way is not served.
    request += 1
    clearTimeout(accountWait)
    clearTimeout(refreshTimer)
    document.removeEventListener('visibilitychange', scheduleRefresh)
    unwatch?.()

    await sdk?.OpenFeature.clearProviders()
  }

  return {
    read,
    inspect,

    get status() {
      return state.status
    },

    get disabledReason() {
      return state.disabledReason
    },

    // Datadog's answer for the session's context is being served. STALE is one served from cache
    // for that same context, so its values still hold.
    get isReady() {
      return isServing(state.status)
    },

    get lastError() {
      return state.lastError
    },

    get contextSignature() {
      return state.contextSignature
    },

    // Settles once the provider is set up, or once it is clear that it will not be.
    started: start(),

    stop
  }
}

/**
 * `mapGetters` for flags: `{ computedName: datadogKey }` -> computed properties that read the flag
 * with the fallback from `FEATURE_FLAG_KEYS`.
 */
export function mapFeatureFlags(map) {
  return Object.fromEntries(
    Object.entries(map).map(([name, key]) => [
      name,
      function () {
        return this.$featureFlag(key)
      }
    ])
  )
}

export default {
  install(Vue, { store, ...options }) {
    const featureFlags = createFeatureFlags(store, options)

    // Status and diagnostics. Views read flags through `$featureFlag`.
    Vue.prototype.$featureFlags = featureFlags
    Vue.prototype.$featureFlag = (key, fallback) => featureFlags.read(key, fallback)
  }
}
